import { cn } from "@boveda/shared";
import { AppSidebar } from "@/components/app-sidebar";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
import { useNavDrawerSwipe } from "@/features/layout/hooks/use-nav-drawer-swipe";

/**
 * Panel transform. Open and at rest it yields exactly `translate3d(0, 0, 0)`:
 * the drag only shows up in the style while there *is* a drag, so the resting
 * state stays a single comparable value.
 */
function panelTransform(isOpen: boolean, dragPx: number): string {
  if (!isOpen) {
    return "translate3d(-100%, 0, 0)";
  }
  return dragPx === 0 ? "translate3d(0, 0, 0)" : `translate3d(${dragPx}px, 0, 0)`;
}

interface AppLayoutSidebarProps {
  isOpen: () => boolean;
  setOpen: (open: boolean) => void;
}

export function AppLayoutSidebar(props: AppLayoutSidebarProps) {
  let panel: HTMLElement | undefined;

  // Swipe left to dismiss; swiping right does nothing (see the hook). The
  // gesture listens on the BOX, not the panel, so dragging from the dimmed
  // backdrop works too.
  const swipe = useNavDrawerSwipe({
    isOpen: () => props.isOpen(),
    close: () => {
      props.setOpen(false);
    },
    panel: () => panel,
  });

  return (
    <>
      {/* ── Mobile Sidebar Drawer ─────────────────────────────────*/}
      <div
        ref={(el) => {
          swipe.attach(el);
        }}
        data-testid="mobile-nav-drawer"
        class={cn(
          // `touch-pan-y`: the browser keeps vertical scrolling and yields the
          // horizontal axis. Without it, it competes and the drag gets cut off.
          "fixed inset-0 z-[100] overflow-hidden touch-pan-y transition-all duration-300 ease-swift lg:hidden",
          props.isOpen() ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <button
          type="button"
          data-testid="mobile-nav-backdrop"
          onClick={() => {
            props.setOpen(false);
          }}
          class={cn(
            "absolute inset-0 h-full w-full cursor-default border-none bg-black/40 p-0 backdrop-blur-[2px] ease-swift",
            // While the finger is in charge the backdrop obeys the finger: the
            // transition is off and the drag sets the opacity.
            swipe.isDragging() ? "transition-none" : "transition-opacity duration-300",
            props.isOpen() ? "opacity-100" : "opacity-0",
          )}
          style={{ opacity: swipe.isDragging() ? String(swipe.backdropOpacity()) : undefined }}
          // oxlint-disable-next-line lingui/no-unlocalized-strings -- decorative backdrop, label is a11y fallback
          aria-label="Close sidebar"
        />
        <aside
          ref={panel}
          data-testid="mobile-nav-aside"
          class={cn(
            "absolute bottom-0 left-0 top-0 flex w-[280px] flex-col border-r border-border/40 bg-card/85 shadow-2xl backdrop-blur-md ease-swift will-change-transform",
            "pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]",
            // With the transition on, the panel would arrive 300ms after the
            // finger is already somewhere else.
            swipe.isDragging() ? "transition-none" : "transition-transform duration-300",
          )}
          style={{
            transform: panelTransform(props.isOpen(), swipe.dragPx()),
          }}
        >
          <AppSidebar
            onCloseSidebar={() => {
              props.setOpen(false);
            }}
          />
        </aside>
      </div>

      {/* ── Desktop Sidebar ─────────────────────────────────────*/}
      {/* `app-shell-sidebar`: ancla del `MutationObserver` de la sonda de I1
          (P23 / CC-60). Es el chrome estático del shell, así que su ausencia
          en el DOM mide EXACTAMENTE la suspensión que se quiere prohibir. */}
      <aside
        data-testid="app-shell-sidebar"
        class="hidden w-64 shrink-0 flex-col border-r border-border/40 bg-card/10 backdrop-blur-md lg:flex"
      >
        <AppSidebar />
      </aside>

      <AccountSwitcherHost />
    </>
  );
}
